"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { AssetImage } from "@/components/optilase/asset-image";
import { scrollToAdjacent } from "@/components/optilase/carousel-page";
import { surfaceFrom } from "@/components/optilase/surface";
import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname, type Region } from "@/lib/region";
import { resolveRichTextHref } from "@/lib/storyblok-link";
import { useRovingFocus } from "@/lib/use-roving-focus";
import { usePublicPathname } from "@/lib/public-pathname";

type Asset = { filename?: string; alt?: string; focus?: string };

type RichTextMark = {
  type: string;
  attrs?: { href?: string; target?: string };
};
type RichTextNode = {
  type: string;
  text?: string;
  content?: RichTextNode[];
  marks?: RichTextMark[];
};
type RichTextDoc = { type: "doc"; content?: RichTextNode[] };

type StepByStepStep = SbBlokData & {
  title?: string;
  subtitle?: string;
  description?: string | RichTextDoc;
  image?: Asset;
};

type StepByStepBlok = SbBlokData & {
  card_bg_color?: unknown;
  card_style?: string;
  title?: string;
  description?: string;
  steps?: StepByStepStep[];
  bg_color?: string;
};

function richTextHasContent(node: RichTextNode | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return !!node.text?.trim();
  return (node.content ?? []).some(richTextHasContent);
}

function renderStepNode(node: RichTextNode, key: number, region?: Region): ReactNode {
  switch (node.type) {
    case "paragraph":
      return <p key={key}>{node.content?.map((n, i) => renderStepNode(n, i, region))}</p>;
    case "hard_break":
      return <br key={key} />;
    case "text": {
      let el: ReactNode = node.text ?? "";
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") el = <strong>{el}</strong>;
        else if (mark.type === "italic") el = <em>{el}</em>;
        else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
          const href = resolveRichTextHref(mark.attrs.href, region);
          const isExternal = /^https?:\/\//i.test(href);
          const target = isExternal ? "_blank" : mark.attrs.target;
          el = (
            <a
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className="text-teal-08 underline underline-offset-2 hover:opacity-80"
            >
              {el}
            </a>
          );
        }
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return node.content?.map((n, i) => renderStepNode(n, i, region)) ?? null;
  }
}

function StepText({ value, region }: { value: string | RichTextDoc; region: Region }) {
  if (typeof value === "string") {
    return <p className="whitespace-pre-line">{value}</p>;
  }
  if (!richTextHasContent(value)) return null;
  return (
    <div className="flex flex-col gap-3">
      {value.content?.map((n, i) => renderStepNode(n, i, region))}
    </div>
  );
}

function StepDescription({
  value,
  isOpen,
  region,
}: {
  value: string | RichTextDoc;
  isOpen: boolean;
  region: Region;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    if (!isOpen && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [isOpen]);

  let inner: ReactNode = null;
  if (typeof value === "string") {
    inner = <p className="whitespace-pre-line">{value}</p>;
  } else if (richTextHasContent(value)) {
    inner = (
      <div className="flex flex-col gap-3">
        {value.content?.map((n, i) => renderStepNode(n, i, region))}
      </div>
    );
  }

  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const check = () => setScrollable(content.offsetHeight > el.clientHeight + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  if (!inner) return null;
  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        data-step-scroll
        tabIndex={scrollable ? 0 : -1}
        role={scrollable ? "group" : undefined}
        aria-label={scrollable ? "Step description, scroll for more" : undefined}
        className={`text-body h-full touch-pan-y [scrollbar-width:none] scrollbar-none overflow-y-auto overscroll-contain pb-14 leading-[1.56] outline-none [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${
          scrollable
            ? "focus-visible:outline-teal-04 focus-visible:rounded-[8px] focus-visible:outline-2 focus-visible:-outline-offset-2"
            : ""
        }`}
      >
        <div ref={contentRef}>{inner}</div>
      </div>
      {scrollable && (
        <div
          aria-hidden
          className="from-navy-03 pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-linear-to-t to-transparent"
        />
      )}
    </div>
  );
}

export default function StepByStep({ blok }: { blok: StepByStepBlok }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const steps = blok.steps || [];
  const { bg, hex } = resolveBg(blok.bg_color, "teal-02");
  const cardSurface = surfaceFrom(blok.card_bg_color);
  const plain = blok.card_style === "plain";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Face: any = plain ? "div" : "button";
  const region = regionFromPathname(usePublicPathname() ?? "/");

  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const i = openIndex;
      setOpenIndex(null);
      requestAnimationFrame(() => triggerRefs.current[i]?.focus());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex]);

  const revealCard = (index: number) => {
    const scroller = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!scroller || !card) return;
    const sRect = scroller.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const cardLeft = cRect.left - sRect.left + scroller.scrollLeft;
    const target = cardLeft + cRect.width / 2 - scroller.clientWidth / 2;
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: "smooth",
    });
  };

  const handleTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const total = triggerRefs.current.length;
    if (total === 0) return;
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % total;
    else if (e.key === "ArrowLeft") next = (index - 1 + total) % total;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = total - 1;
    else return;
    e.preventDefault();
    setActiveIndex(next);
    triggerRefs.current[next]?.focus({ preventScroll: true });
    revealCard(next);
  };

  const scrollByPage = (dir: number) => {
    const el = scrollerRef.current;
    if (el) scrollToAdjacent(el, dir > 0 ? 1 : -1);
  };

  const { ref: arrowsRef, rovingProps: arrowsRovingProps } = useRovingFocus<HTMLDivElement>();

  const arrowClass =
    "flex size-12 items-center justify-center rounded-full border border-teal-04 text-teal-07 transition-colors hover:border-teal-07 hover:bg-teal-01 aria-disabled:pointer-events-none aria-disabled:opacity-40";

  useEffect(() => {
    if (openIndex === null) return;
    const card = cardRefs.current[openIndex];
    const scroller = scrollerRef.current;
    if (!card || !scroller) return;

    const isLg = window.matchMedia("(min-width: 1024px)").matches;
    const expandedWidth = isLg ? 588 : window.innerWidth - 40;

    const cardRect = card.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const cardLeftInScroller = cardRect.left - scrollerRect.left + scroller.scrollLeft;
    const finalTarget = cardLeftInScroller + expandedWidth / 2 - scroller.clientWidth / 2;

    const startScroll = scroller.scrollLeft;
    const duration = 700;
    let startTs: number | null = null;
    let rafId = 0;

    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const desired = startScroll + (finalTarget - startScroll) * easeOut(progress);
      const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      scroller.scrollLeft = Math.max(0, Math.min(desired, max));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [openIndex]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = steps.length - 1;
      setActiveIndex(closest);
      setAtStart(scroller.scrollLeft <= 1);
      setAtEnd(scroller.scrollLeft >= maxScroll - 1);
      const first = cardRefs.current[0];
      const last = cardRefs.current[steps.length - 1];
      if (first && last) {
        const span = last.offsetLeft + last.offsetWidth - first.offsetLeft;
        setIsScrollable(span > scroller.clientWidth + 1);
      } else {
        setIsScrollable(false);
      }
    };

    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [steps.length, openIndex]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const inner = (e.target as HTMLElement)?.closest?.(
        "[data-step-scroll]",
      ) as HTMLElement | null;
      if (inner && inner.scrollHeight > inner.clientHeight) return;
      e.preventDefault();
      window.scrollBy({ top: e.deltaY });
    };

    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", onWheel);
  }, []);

  if (steps.length === 0) return null;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="py-10 md:py-10 lg:py-20">
        <div className="px-5 md:px-10 lg:px-20">
          <div className="mx-auto flex w-full max-w-7xl items-start gap-6">
            <div className="flex flex-1 flex-col gap-5">
              {blok.title && <h2 className="text-section text-teal-08">{blok.title}</h2>}
              {blok.description && (
                <p className="text-grey-07 max-w-[997px] text-base leading-relaxed tracking-[-0.01em] lg:text-xl">
                  {blok.description}
                </p>
              )}
            </div>
            {isScrollable && (
              <div
                ref={arrowsRef}
                {...arrowsRovingProps}
                className="hidden shrink-0 items-center gap-2 lg:flex"
              >
                <button
                  type="button"
                  data-roving
                  onClick={() => scrollByPage(-1)}
                  aria-disabled={atStart || undefined}
                  aria-label="Previous steps"
                  className={arrowClass}
                >
                  <ChevronLeft className="size-5" strokeWidth={2} />
                </button>
                <button
                  type="button"
                  data-roving
                  onClick={() => scrollByPage(1)}
                  aria-disabled={atEnd || undefined}
                  aria-label="Next steps"
                  className={arrowClass}
                >
                  <ChevronRight className="size-5" strokeWidth={2} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 lg:mt-10">
          <div className="relative lg:mx-auto lg:max-w-360 lg:px-20">
            <div
              ref={scrollerRef}
              tabIndex={isScrollable ? 0 : -1}
              role={isScrollable ? "region" : undefined}
              aria-label={isScrollable ? blok.title || "Steps" : undefined}
              className={`focus-visible:outline-teal-07 touch-pan-x touch-pan-y scrollbar-none overflow-y-clip py-1 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 [&::-webkit-scrollbar]:hidden ${
                isScrollable || openIndex !== null ? "overflow-x-auto" : "overflow-x-clip"
              }`}
            >
              <ul
                className={`flex w-max items-start gap-[25px] px-5 pt-[42px] md:px-10 lg:gap-6 lg:px-0 ${
                  steps.length >= 4 ? "mx-auto" : "lg:mx-0"
                }`}
              >
                {steps.map((step, i) => {
                  const isOpen = !plain && openIndex === i;
                  const stepNumber = String(i + 1).padStart(2, "0");

                  return (
                    <li
                      key={step._uid as string}
                      ref={(el) => {
                        cardRefs.current[i] = el;
                      }}
                      {...storyblokEditable(step)}
                      className={`relative shrink-0 transition-[width] duration-700 ease-out perspective-[1500px] ${
                        isOpen ? "w-[calc(100vw-2.5rem)] lg:w-[588px]" : "w-[280px] lg:w-[300px]"
                      }`}
                    >
                      <div
                        className={`pointer-events-none absolute top-[-37px] left-1/2 z-10 flex size-[74px] -translate-x-1/2 items-center justify-center rounded-full transition-colors duration-300 lg:top-[-42px] lg:size-[83px] ${
                          isOpen ? "bg-white" : "bg-teal-08"
                        }`}
                      >
                        <span
                          className={`lg:text-h4 text-[28px] leading-none font-semibold tracking-[0.02em] transition-colors duration-300 ${
                            isOpen ? "text-teal-08" : "text-white"
                          }`}
                        >
                          {stepNumber}
                        </span>
                      </div>

                      <div
                        className={`relative h-105 transition-transform duration-700 ease-out transform-3d lg:h-[482px] ${
                          isOpen ? "transform-[rotateY(180deg)]" : ""
                        }`}
                      >
                        <Face
                          {...(plain
                            ? {}
                            : {
                                ref: (el: HTMLButtonElement | null) => {
                                  triggerRefs.current[i] = el;
                                },
                                type: "button" as const,
                                onClick: () => setOpenIndex(i),
                                onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) =>
                                  handleTriggerKey(e, i),
                                "aria-expanded": isOpen,
                                "aria-hidden": isOpen,
                                tabIndex: !isOpen && i === 0 ? 0 : -1,
                              })}
                          className={`absolute inset-y-0 left-0 flex w-[280px] flex-col overflow-hidden rounded-[18px] text-left backface-hidden lg:w-[300px] lg:rounded-[20px] ${
                            plain ? "" : "video-poster-btn cursor-pointer"
                          }`}
                        >
                          <div className="bg-teal-02 relative h-[194px] w-full shrink-0 lg:h-[216px]">
                            {step.image?.filename && (
                              <AssetImage
                                src={step.image.filename}
                                focus={step.image.focus}
                                alt={step.image.alt || ""}
                                fill
                                sizes="280px"
                                className="object-cover"
                              />
                            )}
                          </div>

                          <div
                            style={
                              cardSurface
                                ? { backgroundColor: cardSurface.backgroundColor }
                                : undefined
                            }
                            className={`flex flex-1 flex-col px-6 pt-8 pb-6 lg:px-8 lg:pt-10 lg:pb-8 ${
                              cardSurface ? "" : "bg-white"
                            }`}
                          >
                            {step.title && (
                              <p
                                className={`text-grey-09 leading-[1.2] font-bold ${
                                  plain ? "text-2xl" : "text-h6 lg:text-h5"
                                }`}
                              >
                                {step.title}
                              </p>
                            )}
                            {step.subtitle && (
                              <p className="text-grey-06 mt-2 text-base leading-relaxed">
                                {step.subtitle}
                              </p>
                            )}
                            {plain ? (
                              step.description ? (
                                <div className="text-grey-06 mt-4 text-base leading-relaxed tracking-[-0.01em]">
                                  <StepText value={step.description} region={region} />
                                </div>
                              ) : null
                            ) : (
                              <span className="text-teal-08 mt-auto pt-4 text-base leading-[1.054] font-bold">
                                Learn more
                              </span>
                            )}
                          </div>
                        </Face>

                        {plain ? null : (
                          <div
                            inert={!isOpen}
                            className="bg-navy-03 absolute inset-0 flex transform-[rotateY(180deg)] flex-col gap-4 rounded-[18px] px-7 pt-16 pb-9 text-white backface-hidden lg:rounded-[20px] lg:px-11 lg:pt-16 lg:pb-11"
                          >
                            {step.title && (
                              <p className="text-teal-05 text-base leading-[1.12] font-bold">
                                {step.title}
                              </p>
                            )}
                            {step.description && (
                              <StepDescription
                                value={step.description}
                                isOpen={isOpen}
                                region={region}
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setOpenIndex(null);
                                requestAnimationFrame(() => triggerRefs.current[i]?.focus());
                              }}
                              tabIndex={isOpen ? 0 : -1}
                              className="text-teal-05 cursor-pointer self-start text-base leading-[1.054] font-bold hover:underline"
                            >
                              Close
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div
              aria-hidden
              className={`pointer-events-none absolute inset-y-0 left-0 z-20 hidden w-16 transition-opacity duration-200 lg:left-20 lg:block ${
                isScrollable && !atStart ? "opacity-100" : "opacity-0"
              }`}
              style={{ backgroundImage: `linear-gradient(to right, ${hex}, transparent)` }}
            />
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-y-0 right-0 z-20 hidden w-16 transition-opacity duration-200 lg:right-20 lg:block ${
                isScrollable && !atEnd ? "opacity-100" : "opacity-0"
              }`}
              style={{ backgroundImage: `linear-gradient(to left, ${hex}, transparent)` }}
            />
          </div>

          {isScrollable && (
            <div className="mt-6 flex justify-center lg:hidden">
              <div className="flex items-center">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    tabIndex={-1}
                    onClick={() => {
                      cardRefs.current[i]?.scrollIntoView({
                        behavior: "smooth",
                        inline: "center",
                        block: "nearest",
                      });
                    }}
                    aria-label={`Go to step ${i + 1}`}
                    className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                  >
                    <span
                      aria-hidden
                      className={`block h-1.5 rounded-full transition-all duration-300 ${
                        activeIndex === i ? "bg-teal-07 w-8" : "bg-teal-03 w-2"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
